<script lang="ts" setup>
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import { useLocale } from '../../composables/useLocale';
import { SideBarEntityType } from '../../composables/useSideBar';

const { t } = useLocale();

const props = withDefaults(
  defineProps<{
    title: string;
    subtitle: string;
    isLoading: boolean;
    selectedEntity: SideBarEntityType;
    initials?: string;
    icon?: string;
    noIcon?: boolean;
    titleAction?: () => void;
    subtitleAction?: () => void;
    onClose?: () => void;
  }>(),
  {
    noIcon: false,
    titleAction: undefined,
    subtitleAction: undefined,
    onClose: undefined,
  },
);

const { smAndDown } = useDisplay();

// The row is a button, so its name has to start with what the row shows, in the order it shows it:
// speech input acts on the words the user can read, and a name that leaves the subtitle out — or
// puts the action in front of it — is no longer the visible label. What it does comes last.
const editLabel = computed(() => [props.title, props.subtitle, t('lcp.edit')].filter(Boolean).join(' '));
</script>

<template>
  <v-skeleton-loader v-if="isLoading" type="avatar, list-item-two-line" class="py-1"></v-skeleton-loader>
  <v-row v-if="!isLoading" no-gutters class="px-4 py-2 ga-2">
    <v-col v-if="smAndDown" class="d-flex flex-grow-0 align-center">
      <v-square-btn
        class="pa-2"
        height="48"
        variant="text"
        icon="mdi-chevron-left"
        @click="onClose"
        :aria-label="t('lcp.back') + ' ' + t('lcp.close_side_panel')"
      />
    </v-col>
    <v-col>
      <v-list-item
        :link="!!titleAction"
        rounded
        @click="titleAction"
        height="56"
        class="px-0"
        :class="{ 'text-center': smAndDown, 'pl-1': smAndDown }"
        :role="titleAction ? 'button' : undefined"
        :aria-label="titleAction ? editLabel : undefined"
      >
        <template #title>
          <div class="d-flex align-center text-truncates w-100" :class="{ 'justify-center': smAndDown }">
            <div
              class="text-truncate vertical-center text-subtitle-1 font-weight-bold"
              :style="{ maxWidth: `calc(${!smAndDown ? '100% - 20px' : ''})`, display: 'inline-block' }"
            >
              {{ title }}
            </div>
            <v-icon v-if="titleAction" icon="mdi-pencil" class="text-primary pl-2" size="sm" />
          </div>
          <div v-if="subtitle && subtitleAction">
            <v-btn variant="text" color="primary" class="pa-0" height="24" @click.stop="subtitleAction">
              <span>
                {{ subtitle }}
              </span>
            </v-btn>
          </div>
        </template>
        <template v-if="subtitle && !subtitleAction" #subtitle>
          <div class="text-truncate">
            {{ subtitle }}
          </div>
        </template>
        <template v-if="!smAndDown" #prepend>
          <div
            v-if="!noIcon"
            class="bg-primary-container text-on-primary-container d-flex align-center justify-center text-caption font-weight-bold rounded-circle pa-1 ml-2 mr-4"
            style="width: 34px; height: 34px"
          >
            <v-icon v-if="icon && !initials" :icon="icon" />
            <template v-if="initials">{{ initials }}</template>
          </div>
        </template>
      </v-list-item>
    </v-col>

    <v-col v-if="!smAndDown" cols="auto" class="d-flex align-center">
      <v-square-btn
        class="text-primary"
        variant="text"
        icon="mdi-window-close"
        height="48"
        @click="onClose"
        :aria-label="t('lcp.close')"
      />
    </v-col>
  </v-row>
</template>
